import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { THEME_PREFERENCES, ThemePreference, ThemeService } from '@core/services/theme.service';
import { IconComponent, IconName } from '@shared/icons/icon/icon.component';

const ICON: Record<ThemePreference, IconName> = { dark: 'moon', light: 'sun', system: 'monitor' };
const LABEL_KEY: Record<ThemePreference, string> = {
  dark: 'THEME.DARK',
  light: 'THEME.LIGHT',
  system: 'THEME.SYSTEM',
};

/** Three-state topbar button: dark (moon) → light (sun) → system (screen). */
@Component({
  selector: 'mtm-theme-toggle',
  standalone: true,
  imports: [IconComponent, TranslatePipe],
  template: `
    <button
      type="button"
      class="theme-toggle"
      [attr.aria-label]="
        'THEME.SWITCH'
          | translate: { current: (currentKey() | translate), next: (nextKey() | translate) }
      "
      [title]="(currentKey() | translate) + ' · ' + ('THEME.LABEL' | translate)"
      (click)="theme.cycle()"
    >
      <mtm-icon [name]="icon()" [size]="16" />
    </button>
  `,
  styles: `
    .theme-toggle {
      width: 38px;
      height: 38px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--mtm-radius-md);
      border: 1px solid var(--mtm-border);
      background: var(--mtm-fill);
      color: var(--mtm-text-secondary);
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      &:hover {
        background: var(--mtm-fill-hover);
        color: var(--mtm-text);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemeToggleComponent {
  readonly theme = inject(ThemeService);

  readonly icon = computed(() => ICON[this.theme.preference()]);
  readonly currentKey = computed(() => LABEL_KEY[this.theme.preference()]);
  readonly nextKey = computed(() => {
    const i = THEME_PREFERENCES.indexOf(this.theme.preference());
    return LABEL_KEY[THEME_PREFERENCES[(i + 1) % THEME_PREFERENCES.length]];
  });
}
